export const FINANCING_INTEREST_VERSIONS = ['commbank', 'brighte'] as const;

export type FinancingInterestVersion = (typeof FINANCING_INTEREST_VERSIONS)[number];

export type FinancingInterestIcon = 'document' | 'mouse';

export type FinancingInterestBenefit =
  | {
      kind: 'icon';
      icon: FinancingInterestIcon;
      title: string;
      description: string;
    }
  | {
      kind: 'check';
      description: string;
    };

export type FinancingInterestContent = {
  version: FinancingInterestVersion;
  headerLogo: {
    provider: FinancingInterestVersion;
    alt: string;
  };
  heading: string;
  intro: string;
  introEmphasis?: string;
  benefits: FinancingInterestBenefit[];
  callout?: {
    title: string;
    description: string;
  };
  footer?: string;
};

const CONTENT: Record<FinancingInterestVersion, FinancingInterestContent> = {
  commbank: {
    version: 'commbank',
    headerLogo: {
      provider: 'commbank',
      alt: 'CommBank logo',
    },
    heading: 'Interested in financing?',
    intro:
      "Once you've selected and confirmed your quotes, you'll have the chance to explore financing options with CommBank.",
    benefits: [
      {
        kind: 'icon',
        icon: 'document',
        title: 'Cashback finance options',
        description: 'Get cashback and discounted interest rates with CommBank financing options.',
      },
      {
        kind: 'icon',
        icon: 'mouse',
        title: 'Quick and easy application',
        description: "Complete your application online and you'll get a response within minutes.",
      },
    ],
    footer: 'To find out more about CommBank financing offers, visit CommBank Home Energy Upgrades.',
  },
  brighte: {
    version: 'brighte',
    headerLogo: {
      provider: 'brighte',
      alt: 'Brighte logo',
    },
    heading: 'Interested in finance?',
    intro: 'Choose a brighter way to pay, and join over 180,000 customers financing their upgrades with Brighte.',
    introEmphasis: '180,000 customers',
    benefits: [
      {
        kind: 'check',
        description: 'Reduce the upfront costs',
      },
      {
        kind: 'check',
        description: 'Simple fortnightly repayments',
      },
      {
        kind: 'check',
        description: 'Access government rebates and programs',
      },
    ],
    callout: {
      title: 'How it works:',
      description: "It's easy, your selected Brighte vendor will share options with you and walk you through the process.",
    },
  },
};

function getVersionValue(stepConfiguration: unknown): unknown {
  if (!stepConfiguration || typeof stepConfiguration !== 'object') return undefined;
  const financingInterest = (stepConfiguration as Record<string, unknown>).financingInterest;
  if (!financingInterest || typeof financingInterest !== 'object') return undefined;
  return (financingInterest as Record<string, unknown>).version;
}

export function getFinancingInterestVersion(stepConfiguration: unknown): FinancingInterestVersion {
  const version = getVersionValue(stepConfiguration);

  if (typeof version !== 'string' || version.length === 0) {
    throw new Error('Missing financingInterest.version for financing-interest step');
  }

  const supportedVersion = FINANCING_INTEREST_VERSIONS.find((allowed) => allowed === version);
  if (!supportedVersion) {
    throw new Error(`Unsupported financingInterest.version "${version}"`);
  }

  return supportedVersion;
}

export function getFinancingInterestContent(stepConfiguration: unknown): FinancingInterestContent {
  return CONTENT[getFinancingInterestVersion(stepConfiguration)];
}
